<script setup lang="ts">
/**
 * HLS Video Player using Shaka Player.
 * Always uses Shaka for HLS playback.
 */
import type {
	ShakaErrorDetail,
	ShakaPlayer
} from '~/composables/useHlsPlayer/types';

import {
	breakpointsTailwind,
	useBreakpoints,
	useDebounceFn,
	useEventListener,
	whenever
} from '@vueuse/core';
import {
	registerActivePlayer,
	startBandwidthProbing,
	stopBandwidthProbingIfIdle,
	unregisterActivePlayer
} from '~/composables/useHlsPlayer/shakaLoader';
import {
	createPlayer,
	destroyPlayer
} from '~/composables/useSharedVideoPlayer';
import { getProxiedVideoUrl } from '~/composables/useVideoUrls';
import { ErrorCategory } from '~/core/ports/datadog';
import { isIOSLikeSafariBrowser, isSafariBrowserAgent } from '~/utils';
import { useDatadog } from '~~/composables/useDatadog';

import { GlassButton } from '~/components/GlassButton';
import VideoProgressBar from '~/components/VideoCard/VideoProgressBar.vue';

import IconMaterialSymbolsLightPlayArrow from '~icons/material-symbols-light/play-arrow';
import IconMaterialSymbolsLightVolumeOff from '~icons/material-symbols-light/volume-off';
import IconMaterialSymbolsLightVolumeUp from '~icons/material-symbols-light/volume-up';

import { useIosQualityUpgrade } from './useIosQualityUpgrade';

interface HlsVideoPlayerProps {
	/** Raw video URL; null/undefined/empty are treated as “no source” (player will not load). */
	videoUrl?: string | null;
	posterUrl?: string;
	autoplay?: boolean;
	muted?: boolean;
	loop?: boolean;
	controls?: boolean;
	mediaFit?: 'cover' | 'contain';
	showProgressBar?: boolean;
	showProgressBarOnHover?: boolean;
	showPlayPauseControl?: boolean;
	interactionMode?: 'toggle-play' | 'emit-click';
}

const props = withDefaults(defineProps<HlsVideoPlayerProps>(), {
	autoplay: true,
	muted: true,
	loop: false,
	controls: true,
	mediaFit: 'cover',
	showProgressBar: false,
	showProgressBarOnHover: false,
	showPlayPauseControl: false,
	interactionMode: 'toggle-play'
});

const emit = defineEmits<{
	click: [];
}>();

const { trackError } = useDatadog();

const videoRef = ref<HTMLVideoElement | null>(null);
const {
	fetchVariants: iosFetchVariants,
	startUpgrade: iosStartUpgrade,
	stopUpgrade: iosStopUpgrade,
	markWaiting: iosMarkWaiting,
	abortUpgrade: iosAbortUpgrade,
	subscribeToProbe: iosSubscribeToProbe
} = useIosQualityUpgrade(videoRef);
const player = shallowRef<ShakaPlayer | null>(null);
const cleanupFns: Array<() => void> = [];
let networkErrorReinitTimer: ReturnType<typeof setTimeout> | null = null;

function registerCleanup(cleanupFn?: (() => void) | void) {
	if (!cleanupFn) return;
	cleanupFns.push(cleanupFn);
}

const isReady = ref(false);
const hasError = ref(false);
const isVideoLoaded = ref(false);
const playing = ref(false);
const hasStartedPlaying = ref(false);
const isAutoplayBlocked = ref(false);
const progress = ref(0);
const proxiedVideoUrl = computed(() => getProxiedVideoUrl(props.videoUrl));
const mediaFitClass = computed(() =>
	props.mediaFit === 'contain' ? 'object-contain' : 'object-cover'
);

const isMuted = ref(props.muted);
const breakpoints = import.meta.client
	? useBreakpoints(breakpointsTailwind)
	: null;
const isTabletOrMobileViewport = computed(
	() => breakpoints?.smaller('xl').value ?? false
);
const isSafariBrowser = computed(() => {
	if (!import.meta.client) return false;

	return isSafariBrowserAgent(navigator.userAgent, navigator.vendor);
});
const isIOSLikeSafari = computed(() => {
	if (!import.meta.client || !isSafariBrowser.value) return false;

	return isIOSLikeSafariBrowser({
		userAgent: navigator.userAgent,
		vendor: navigator.vendor,
		platform: navigator.platform,
		maxTouchPoints: navigator.maxTouchPoints
	});
});
const shouldForceVisibleControls = computed(
	() => isSafariBrowser.value && isTabletOrMobileViewport.value
);
const shouldUseNativeHls = computed(
	() => isIOSLikeSafari.value && isTabletOrMobileViewport.value
);
const shouldUseSimplifiedControlStyles = computed(
	() => shouldForceVisibleControls.value
);
const shouldUseHoverControls = computed(
	() =>
		props.showProgressBarOnHover &&
		!isTabletOrMobileViewport.value &&
		!shouldForceVisibleControls.value
);
const overlayVisibilityClasses = computed(() =>
	shouldUseHoverControls.value
		? 'opacity-100 pointer-events-auto transition-opacity duration-200 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:pointer-events-none group-hover:[@media(hover:hover)]:opacity-100 group-hover:[@media(hover:hover)]:pointer-events-auto'
		: 'opacity-100 pointer-events-auto'
);
const controlsOverlayClasses = computed(() =>
	props.showProgressBar
		? `absolute inset-x-4 bottom-[68px] z-20 flex items-center gap-3 md:inset-x-5 md:bottom-20 lg:inset-x-12 lg:bottom-24 ${overlayVisibilityClasses.value}`
		: `absolute inset-x-4 bottom-4 z-20 flex items-center gap-3 ${overlayVisibilityClasses.value}`
);
const plainControlButtonClass =
	'flex size-8 shrink-0 items-center justify-center rounded-full border border-white/20 bg-[rgba(60,60,60,0.72)] text-white';
const shouldRenderControlsOverlay = computed(() => {
	if (!proxiedVideoUrl.value) return false;

	if (shouldForceVisibleControls.value) {
		return props.controls || props.showProgressBar;
	}

	return (
		(props.controls || props.showProgressBar) &&
		!hasError.value &&
		(isReady.value || isVideoLoaded.value || !!videoRef.value)
	);
});

registerCleanup(
	watch(
		() => props.muted,
		val => {
			isMuted.value = val;
		}
	)
);

function toggleMute() {
	isMuted.value = !isMuted.value;
}

function syncProgress() {
	const video = videoRef.value;
	if (!video || !video.duration || Number.isNaN(video.duration)) {
		progress.value = 0;
		return;
	}

	progress.value = (video.currentTime / video.duration) * 100;
}

const updateProgress = useDebounceFn(syncProgress, 200);

async function togglePlay() {
	const video = videoRef.value;
	if (!video) return;

	if (video.paused) {
		try {
			isAutoplayBlocked.value = false;
			await video.play();
		} catch {
			/* user-initiated — silent */
		}
	} else {
		video.pause();
	}
}

function handleContainerClick() {
	if (props.interactionMode === 'emit-click') {
		emit('click');
		return;
	}

	void togglePlay();
}

function seekTo(ratio: number) {
	const video = videoRef.value;
	if (!video || !video.duration || Number.isNaN(video.duration)) return;

	video.currentTime = ratio * video.duration;
	progress.value = ratio * 100;
	updateProgress();
}

async function tryAutoplay() {
	const video = videoRef.value;
	if (
		!video ||
		!props.autoplay ||
		hasError.value ||
		!video.paused ||
		isAutoplayBlocked.value
	)
		return;

	try {
		await video.play();
	} catch (err) {
		if (err instanceof DOMException && err.name === 'NotAllowedError') {
			isAutoplayBlocked.value = true;
			hasStartedPlaying.value = true;
			isVideoLoaded.value = true;
		}
	}
}

function extractPropertyIdFromUrl(videoUrl: string): string | null {
	// Extract from: https://hls.pilarhomes.com.br/portal-property-showcase/{PropertyID}/{UUID}/...
	const match = videoUrl.match(/portal-property-showcase\/([^/]+)\//);
	return match?.[1] ?? null;
}

async function initPlayer() {
	if (!import.meta.client || !videoRef.value) return;

	const url = proxiedVideoUrl.value;
	if (!url) return;

	try {
		if (shouldUseNativeHls.value) {
			videoRef.value.src = url;
			videoRef.value.load();
			isReady.value = true;

			// Fetch variants in background — quality upgrade begins on first canplay
			void iosFetchVariants(url);

			await tryAutoplay();

			return;
		}

		player.value = await createPlayer(url, videoRef.value);

		if (!videoRef.value) {
			destroyPlayer(player.value);
			player.value = null;
			return;
		}

		player.value.addEventListener(
			'error',
			(event: { detail: ShakaErrorDetail }) => {
				const detail = event.detail;
				const isNetworkError = detail?.category === 1;
				if (isNetworkError && proxiedVideoUrl.value) {
					console.warn(
						'[HlsVideoPlayer] Network error, reinitialising silently:',
						detail?.code
					);
					// player.load() cannot clear Shaka's permanent error state after
					// a CRITICAL error — destroy and reinit mirrors ReelVideoPlayer behaviour.
					// Must wait for destroy to complete before nulling to avoid 7003
					// (OBJECT_DESTROYED) when load() is still in-flight.
					destroyPlayer(player.value)
						.then(() => {
							player.value = null;
							clearTimeout(networkErrorReinitTimer ?? undefined);
							networkErrorReinitTimer = setTimeout(() => {
								networkErrorReinitTimer = null;
								void initPlayer();
							}, 500);
						})
						.catch(() => {
							// ignore destroy errors
						});
					return;
				}
				console.error('[HlsVideoPlayer] Shaka error:', detail);

				// Codec not supported by the browser (e.g. H.264 High 10-bit / HEVC).
				// 4032 = CONTENT_UNSUPPORTED_BY_BROWSER (isTypeSupported false),
				// 3016 = VIDEO_ERROR (decoder rejected the stream at runtime).
				const isUnsupportedCodec =
					(detail?.category === 4 && detail?.code === 4032) ||
					(detail?.category === 3 && detail?.code === 3016);
				if (isUnsupportedCodec && proxiedVideoUrl.value) {
					const propertyId = extractPropertyIdFromUrl(proxiedVideoUrl.value);
					trackError({
						message: `[HlsVideoPlayer] Shaka codec error (unsupported, likely High 10-bit) category=${detail?.category} code=${detail?.code} | propertyId=${propertyId} | videoUrl=${proxiedVideoUrl.value}`,
						source: 'source',
						category: ErrorCategory.UI,
						stack: detail?.stack
					});
				} else {
					trackError({
						message: `[HlsVideoPlayer] Shaka error: category=${detail?.category} code=${detail?.code}`,
						source: 'source',
						category: ErrorCategory.UI,
						stack: detail?.stack
					});
				}
				hasError.value = true;
			}
		);

		videoRef.value.loop = props.loop;
		videoRef.value.muted = isMuted.value;

		await tryAutoplay();

		isReady.value = true;
	} catch (error) {
		console.error('[HlsVideoPlayer] Init error:', error);
		hasError.value = true;
	}
}

// Sync muted state with video element
registerCleanup(
	watch(isMuted, newMuted => {
		if (videoRef.value) {
			videoRef.value.muted = newMuted;
		}
	})
);

registerCleanup(
	useEventListener(videoRef, 'canplay', () => {
		isReady.value = true;
		isVideoLoaded.value = true;
		void tryAutoplay();
		// Start quality upgrade monitoring once native HLS has enough data to play
		if (shouldUseNativeHls.value) iosStartUpgrade();
	})
);

registerCleanup(
	useEventListener(videoRef, 'waiting', () => {
		if (shouldUseNativeHls.value) {
			iosMarkWaiting();
			iosStopUpgrade(); // don't upgrade while stalling — restart on next canplay
		}
	})
);

registerCleanup(
	useEventListener(videoRef, 'loadedmetadata', () => {
		isReady.value = true;
		isVideoLoaded.value = true;
		updateProgress();
		void tryAutoplay();
	})
);

registerCleanup(
	useEventListener(videoRef, 'loadeddata', () => {
		isReady.value = true;
		isVideoLoaded.value = true;
		updateProgress();
		void tryAutoplay();
	})
);

registerCleanup(
	useEventListener(videoRef, 'durationchange', () => {
		updateProgress();
	})
);

registerCleanup(
	useEventListener(videoRef, 'timeupdate', () => {
		updateProgress();
	})
);

registerCleanup(
	useEventListener(videoRef, 'error', () => {
		hasError.value = true;
	})
);

registerCleanup(
	useEventListener(videoRef, 'play', () => {
		playing.value = true;
		hasStartedPlaying.value = true;
		if (proxiedVideoUrl.value) registerActivePlayer(proxiedVideoUrl.value);
	})
);

registerCleanup(
	useEventListener(videoRef, 'pause', () => {
		playing.value = false;
		if (proxiedVideoUrl.value) unregisterActivePlayer(proxiedVideoUrl.value);
	})
);

// Listen for reels modal events
let mutedBeforeModalOpen = props.muted;

registerCleanup(
	useEventListener(window, 'reels-modal-opened', () => {
		mutedBeforeModalOpen = isMuted.value;
		isMuted.value = true;
	})
);

registerCleanup(
	useEventListener(window, 'reels-modal-closed', () => {
		isMuted.value = mutedBeforeModalOpen;
	})
);

// Watch for URL changes and reload
registerCleanup(
	whenever(
		() => proxiedVideoUrl.value,
		async newUrl => {
			progress.value = 0;

			if (shouldUseNativeHls.value && videoRef.value && newUrl) {
				iosStopUpgrade();
				void iosFetchVariants(newUrl);
				videoRef.value.src = newUrl;
				videoRef.value.load();

				await tryAutoplay();
			} else if (player.value && newUrl) {
				try {
					await player.value.load(newUrl);
					await tryAutoplay();
				} catch (error) {
					console.error('[HlsVideoPlayer] Error loading new source:', error);
				}
			}
		}
	)
);

// On iOS native path: register for bandwidth probe improvements so a network
// recovery triggers an immediate quality upgrade check without waiting for
// the buffer-growth monitor to accumulate enough samples.
let unsubscribeProbe: (() => void) | null = null;

onMounted(() => {
	initPlayer();
	if (shouldUseNativeHls.value) {
		startBandwidthProbing();
		unsubscribeProbe = iosSubscribeToProbe();
	}
});

onBeforeUnmount(() => {
	cleanupFns.splice(0).forEach(cleanupFn => cleanupFn());

	if (networkErrorReinitTimer) {
		clearTimeout(networkErrorReinitTimer);
		networkErrorReinitTimer = null;
	}

	unsubscribeProbe?.();
	stopBandwidthProbingIfIdle();
	iosAbortUpgrade();
	if (proxiedVideoUrl.value) unregisterActivePlayer(proxiedVideoUrl.value);

	if (shouldUseNativeHls.value && videoRef.value) {
		videoRef.value.pause();
		videoRef.value.removeAttribute('src');
		videoRef.value.load();
	}

	destroyPlayer(player.value);
});

defineExpose({
	playing,
	hasStartedPlaying,
	isAutoplayBlocked,
	progress,
	isMuted,
	hasError,
	pause: () => {
		videoRef.value?.pause();
	},
	toggleMute,
	togglePlay,
	seekTo
});
</script>

<template>
	<div
		class="hls-video-player group relative size-full overflow-hidden bg-black isolate"
		@click="handleContainerClick"
	>
		<!-- Poster Image (shown while video is loading) -->
		<div
			v-if="posterUrl && !isVideoLoaded && !hasError"
			class="absolute inset-0 z-0 size-full overflow-hidden"
		>
			<img
				:src="posterUrl"
				alt="Video thumbnail"
				:class="['size-full border-0 outline-none', mediaFitClass]"
			/>
		</div>

		<!-- Video Element (Shaka manages the source) -->
		<video
			ref="videoRef"
			:autoplay="autoplay"
			:controls="false"
			:muted="isMuted"
			:loop="loop"
			controlslist="nodownload noplaybackrate nofullscreen noremoteplayback"
			disablepictureinpicture
			disableremoteplayback
			preload="metadata"
			playsinline
			webkit-playsinline="true"
			:class="[
				'relative z-10 size-full cursor-pointer',
				mediaFitClass,
				shouldForceVisibleControls && 'safari-video-layer-fix'
			]"
		/>

		<!-- Error State -->
		<div
			v-if="hasError"
			class="absolute inset-0 flex items-center justify-center bg-black/80"
		>
			<span class="text-sm text-white/70">Erro ao carregar vídeo</span>
		</div>

		<!-- Controls -->
		<div
			v-if="shouldRenderControlsOverlay"
			:class="[
				controlsOverlayClasses,
				shouldForceVisibleControls && 'safari-controls-layer-fix'
			]"
			@click.stop
		>
			<div v-if="controls" class="flex shrink-0 items-center gap-2">
				<template v-if="showPlayPauseControl && hasStartedPlaying">
					<button
						v-if="shouldUseSimplifiedControlStyles"
						type="button"
						:class="plainControlButtonClass"
						:aria-label="playing ? 'Pausar vídeo' : 'Reproduzir vídeo'"
						@click="togglePlay"
					>
						<template v-if="playing">
							<span class="sr-only">Pausar vídeo</span>
							<span aria-hidden="true" class="flex items-center gap-0.5">
								<span class="h-3 w-[3px] rounded-full bg-white" />
								<span class="h-3 w-[3px] rounded-full bg-white" />
							</span>
						</template>

						<IconMaterialSymbolsLightPlayArrow
							v-else
							class="size-4 translate-x-[0.5px]"
						/>
					</button>

					<GlassButton
						v-else
						shape="circle"
						class="size-8 shrink-0 text-white"
						:aria-label="playing ? 'Pausar vídeo' : 'Reproduzir vídeo'"
						@click="togglePlay"
					>
						<template v-if="playing">
							<span class="sr-only">Pausar vídeo</span>
							<span aria-hidden="true" class="flex items-center gap-0.5">
								<span class="h-3 w-[3px] rounded-full bg-white" />
								<span class="h-3 w-[3px] rounded-full bg-white" />
							</span>
						</template>

						<IconMaterialSymbolsLightPlayArrow
							v-else
							class="size-4 translate-x-[0.5px]"
						/>
					</GlassButton>
				</template>

				<button
					v-if="shouldUseSimplifiedControlStyles"
					type="button"
					:class="plainControlButtonClass"
					:aria-label="isMuted ? 'Ativar som' : 'Desativar som'"
					@click="toggleMute"
				>
					<IconMaterialSymbolsLightVolumeOff v-if="isMuted" class="size-4" />
					<IconMaterialSymbolsLightVolumeUp v-else class="size-4" />
				</button>

				<GlassButton
					v-else
					shape="circle"
					class="size-8 shrink-0 text-white"
					:aria-label="isMuted ? 'Ativar som' : 'Desativar som'"
					@click="toggleMute"
				>
					<IconMaterialSymbolsLightVolumeOff v-if="isMuted" class="size-4" />
					<IconMaterialSymbolsLightVolumeUp v-else class="size-4" />
				</GlassButton>
			</div>

			<div v-if="showProgressBar" class="min-w-0 flex-1">
				<VideoProgressBar
					:progress="progress"
					:disable-backdrop-blur="shouldUseSimplifiedControlStyles"
					@seek="seekTo"
				/>
			</div>
		</div>
	</div>
</template>

<!-- eslint-disable-next-line custom-rules/no-style-tag -->
<style scoped>
.hls-video-player video::-webkit-media-controls {
	display: none !important;
}

.hls-video-player video::-webkit-media-controls-enclosure {
	display: none !important;
}

.hls-video-player video::-webkit-media-controls-panel {
	display: none !important;
}

.hls-video-player video::-webkit-media-controls-play-button {
	display: none !important;
}

.hls-video-player video::-webkit-media-controls-start-playback-button {
	display: none !important;
	-webkit-appearance: none;
}

.safari-video-layer-fix {
	position: relative;
	z-index: 0 !important;
	transform: translate3d(0, 0, 0);
	-webkit-transform: translate3d(0, 0, 0);
}

.safari-controls-layer-fix {
	z-index: 70;
	transform: translate3d(0, 0, 0);
	-webkit-transform: translate3d(0, 0, 0);
	will-change: transform, opacity;
}
</style>
